/**
 * Push Notifications Service
 *
 * Handles:
 * - Service worker registration
 * - Push subscription management (subscribe / unsubscribe)
 * - Sending subscription to the backend
 * - Requesting notification permission
 *
 * Usage:
 *   import { registerSW, subscribeToPush, unsubscribeFromPush } from '@/lib/pushNotifications';
 *
 *   await registerSW();
 *   const sub = await subscribeToPush();
 */

import API_BASE_URL from '../api/config';

// ── Constants ───────────────────────────────────────────────────────
const SW_PATH = '/sw.js';
const PUSH_API_BASE = `${API_BASE_URL}/api/notifications/push`;
const INVALID_VAPID_PUBLIC_KEYS = new Set(['to_be_assigned', 'changeme', 'placeholder']);
const VALPHA_NOTIFICATION_ICON = '/icons/icon-192x192.png';
const VALPHA_NOTIFICATION_BADGE = '/icons/icon-96x96.png';

// ── Helpers ─────────────────────────────────────────────────────────

/** Check browser support for service workers and push. */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Get the VAPID public key from the backend. */
export async function getVapidPublicKey() {
  const token = localStorage.getItem('token');
  const res = await fetch(`${PUSH_API_BASE}/vapid-key/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to fetch VAPID key');
  }

  const vapidKey = normalizeVapidPublicKey(data.vapid_public_key);
  if (!vapidKey) {
    throw new Error('Push notifications are not configured on this server.');
  }

  return vapidKey;
}

/**
 * Convert a base64-encoded VAPID key to a Uint8Array
 * (required by PushManager.subscribe).
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function normalizeVapidPublicKey(value) {
  if (typeof value !== 'string') return null;

  const normalized = value.trim().replace(/^['"]|['"]$/g, '').replace(/\s+/g, '');
  if (!normalized) return null;
  if (INVALID_VAPID_PUBLIC_KEYS.has(normalized.toLowerCase())) return null;
  if (normalized.startsWith('-----BEGIN')) return null;

  try {
    const decoded = urlBase64ToUint8Array(normalized);
    if (decoded.length !== 65 || decoded[0] !== 0x04) return null;
    return normalized;
  } catch {
    return null;
  }
}

// ── Service Worker Registration ─────────────────────────────────────

let swRegistration = null;

/**
 * Register the service worker.
 * Call this once on app boot (e.g. in main.jsx).
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Push] Service workers not supported');
    return null;
  }

  try {
    swRegistration = await navigator.serviceWorker.register(SW_PATH, {
      scope: '/',
      updateViaCache: 'none',   // always fetch sw.js from network
    });
    console.log('[Push] Service worker registered', swRegistration.scope);

    // Force update check on every page load
    swRegistration.update();

    // Check for updates periodically (every 60 min)
    setInterval(() => swRegistration.update(), 60 * 60 * 1000);

    return swRegistration;
  } catch (err) {
    console.error('[Push] SW registration failed:', err);
    return null;
  }
}

/** Get the current SW registration (lazy). Throws if SW is not registered within 5s. */
async function getRegistration() {
  if (swRegistration) return swRegistration;

  // navigator.serviceWorker.ready never resolves if no SW is registered,
  // so add a timeout to avoid hanging forever.
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Service worker not registered. Please reload the page.')), 5000)
  );
  swRegistration = await Promise.race([navigator.serviceWorker.ready, timeout]);
  return swRegistration;
}

// ── Permission ──────────────────────────────────────────────────────

/**
 * Request notification permission.
 * Returns 'granted' | 'denied' | 'default'.
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}

// ── Subscribe / Unsubscribe ─────────────────────────────────────────

/**
 * Subscribe to push notifications.
 * 1. Requests permission
 * 2. Fetches VAPID key from backend
 * 3. Creates a PushSubscription
 * 4. Sends subscription to backend
 *
 * Returns the PushSubscription or null on failure.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    console.warn('[Push] Push not supported in this browser');
    return null;
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    console.warn('[Push] Notification permission denied');
    return null;
  }

  // Let errors propagate to the caller (usePushNotifications hook) so the
  // error state is set and loading is cleared.
  const vapidKey = await getVapidPublicKey();
  const reg = await getRegistration();

  // Check for existing subscription
  let subscription = await reg.pushManager.getSubscription();

  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
    console.log('[Push] New subscription created');
  } else {
    console.log('[Push] Existing subscription found');
  }

  // Send subscription to backend
  await saveSubscriptionToBackend(subscription);

  return subscription;
}

/**
 * Unsubscribe from push notifications and remove from backend.
 */
export async function unsubscribeFromPush() {
  try {
    const reg = await getRegistration();
    const subscription = await reg.pushManager.getSubscription();

    if (subscription) {
      await removeSubscriptionFromBackend(subscription);
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed');
    }

    return true;
  } catch (err) {
    console.error('[Push] Unsubscribe failed:', err);
    return false;
  }
}

/**
 * Check whether the user is currently subscribed.
 */
export async function isSubscribedToPush() {
  if (!isPushSupported()) return false;
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

// ── Backend API ─────────────────────────────────────────────────────

async function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function saveSubscriptionToBackend(subscription) {
  const headers = await authHeaders();
  const res = await fetch(`${PUSH_API_BASE}/subscribe/`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      user_agent: navigator.userAgent,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Backend subscribe failed (${res.status}): ${text}`);
  }
  console.log('[Push] Subscription saved to backend');
}

async function removeSubscriptionFromBackend(subscription) {
  const headers = await authHeaders();
  const res = await fetch(`${PUSH_API_BASE}/unsubscribe/`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      endpoint: subscription.endpoint,
    }),
  });
  if (!res.ok) {
    console.warn('[Push] Backend unsubscribe failed:', res.status);
  }
}

// ── Local Notification (fallback / testing) ─────────────────────────

/**
 * Show a local notification (not via push — for testing or local alerts).
 */
export async function showLocalNotification(title, options = {}) {
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') return null;

  const reg = await getRegistration();
  const notificationOptions = { ...options };
  delete notificationOptions.icon;
  delete notificationOptions.badge;

  return reg.showNotification(title, {
    vibrate: [100, 50, 100],
    ...notificationOptions,
    icon: VALPHA_NOTIFICATION_ICON,
    badge: VALPHA_NOTIFICATION_BADGE,
  });
}
